import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { ColorPickerPopover } from '@/shared/components/custom/color-picker/ColorPickerPopover';
import { useToast } from '@/shared/hooks/useToast';
import { getColorValue } from '@/core/lib/colors';
import { tagFormSchema, type TagFormValues } from '../schemas';
import { useCreateTag, useUpdateTag } from '../hooks/useTagMutations';
import type { Tag } from '../types';

const DEFAULT_COLOR = 'indigo-violet';

interface TagFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  tag?: Tag;
}

export function TagFormDialog({ open, onOpenChange, projectId, tag }: TagFormDialogProps) {
  const { t } = useTranslation('tags');
  const { toast } = useToast();

  const isEditing = !!tag;
  const createTag = useCreateTag(projectId);
  const updateTag = useUpdateTag(projectId);

  const isPending = createTag.isPending || updateTag.isPending;

  const { control, handleSubmit, reset } = useForm<TagFormValues>({
    resolver: zodResolver(tagFormSchema),
    defaultValues: { name: '', color: DEFAULT_COLOR },
  });

  useEffect(() => {
    if (open) {
      reset(
        tag
          ? { name: tag.name ?? '', color: tag.color ?? DEFAULT_COLOR }
          : { name: '', color: DEFAULT_COLOR },
      );
    }
  }, [open, tag, reset]);

  const onSubmit = async (data: TagFormValues) => {
    try {
      if (isEditing && tag.id) {
        await updateTag.mutateAsync({ tagId: tag.id, payload: data });
        toast.success({ title: t('toast.updateSuccess') });
      } else {
        await createTag.mutateAsync(data);
        toast.success({ title: t('toast.createSuccess') });
      }
      onOpenChange(false);
    } catch {
      // global handler shows error toast
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) reset();
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[420px]" data-testid="tag-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEditing ? t('dialog.editTitle') : t('dialog.createTitle')}</DialogTitle>
          {!isEditing && <DialogDescription>{t('dialog.createDescription')}</DialogDescription>}
        </DialogHeader>

        <form id="tag-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 py-2">
          <div data-testid="tag-form-name">
            <FormFieldInput control={control} name="name" label={t('form.name')} floatingLabel />
          </div>

          <div className="space-y-2" data-testid="tag-form-color">
            <p className="text-sm font-medium text-foreground">{t('form.color')}</p>
            <div className="flex items-center gap-3">
              <Controller
                control={control}
                name="color"
                render={({ field }) => (
                  <ColorPickerPopover
                    value={field.value ?? DEFAULT_COLOR}
                    onSelect={(token) => field.onChange(token)}
                    trigger={
                      <button
                        type="button"
                        aria-label={t('form.colorDescription')}
                        data-testid="tag-color-trigger"
                        className="size-8 rounded-full shrink-0 ring-2 ring-offset-2 ring-offset-background ring-border hover:ring-primary transition-all focus-visible:outline-none focus-visible:ring-primary"
                        style={{
                          background: getColorValue(field.value ?? DEFAULT_COLOR),
                        }}
                      />
                    }
                  />
                )}
              />
              <p className="text-xs text-muted-foreground">{t('form.colorDescription')}</p>
            </div>
          </div>
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleClose(false)}
            data-testid="tag-form-cancel-button"
          >
            {t('dialog.cancel')}
          </Button>

          <Button
            type="submit"
            form="tag-form"
            disabled={isPending}
            data-testid="tag-form-save-button"
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('dialog.saving')}
              </>
            ) : (
              t('dialog.save')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
